import { useState, useEffect, useMemo } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StepProgress } from "../components/StepProgress";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { ArrowRight, ArrowLeft, Cpu, Info, Sparkles, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { updateAssessment, trackEvent, getQuestionsGrouped, getAssessment, getAssessmentById, questionAppliesToScenario, getQuestionFieldOptions } from "../lib/api";
import DemoProfileBanner from "../components/DemoProfileBanner";
import WarehouseGroups from "../components/WarehouseGroups";
import { toast } from "sonner";

const SECTION_LABELS = {
  warehouse_context: "Tell us about your warehouse landscape",
  org: "Warehouse Profile & Volumes",
  inbound: "Inbound Processes",
  outbound: "Outbound Processes",
  inventory: "Internal Movements & Inventory",
  production: "Production & Quality",
  automation: "Smart Warehousing",
  integration: "Integrations & Interfaces",
  reporting: "Reporting & KPIs",
  pain_points: "Business Priorities & Future Strategy",
  migration_landscape: "Current SAP Landscape",
};

function CardSelect({ options, value, onChange, testId, multi = false }) {
  const toggle = (val) => {
    if (multi) {
      const arr = Array.isArray(value) ? value : [];
      onChange(arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);
    } else {
      onChange(val);
    }
  };
  const isSelected = (val) => (multi ? (Array.isArray(value) && value.includes(val)) : value === val);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid={testId}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          data-testid={`${testId}-${opt.value}`}
          onClick={() => toggle(opt.value)}
          className={`text-left p-3 rounded-sm border cursor-pointer transition-colors ${
            isSelected(opt.value)
              ? "border-[#025979] bg-[#E0F2FE]/50"
              : "border-[#E5E7EB] hover:border-[#D1D5DB] hover:bg-gray-50"
          }`}
        >
          <span className="text-sm font-medium text-[#111827] block">{opt.label}</span>
          {opt.hint && <span className="text-xs text-[#6B7280] mt-0.5 block">{opt.hint}</span>}
        </button>
      ))}
    </div>
  );
}

function DynamicField({ question, value, onChange, errors }) {
  const fk = question.field_key;
  const opts = question.answer_options || [];
  const isMulti = question.answer_type === "multi_select";
  const isRequired = question.is_required !== false;
  const testId = `field-${fk}`;
  const error = errors[fk];

  // Special handling for representative_sites (depends on warehouse_types)
  if (fk === "representative_sites") return null; // Rendered separately

  return (
    <div className="mb-5">
      <label className="text-xs font-medium text-[#374151] mb-1.5 block">
        {question.question_text} {isRequired && <span className="text-red-500">*</span>}
        {isMulti && <span className="text-[#9CA3AF] font-normal ml-1">(select all that apply)</span>}
      </label>
      {question.help_text && (
        <p
          data-testid={`helper-${fk}`}
          className="text-[11px] text-[#9CA3AF] -mt-1 mb-2"
        >
          {question.help_text}
        </p>
      )}
      {question.answer_type === "dropdown" ? (
        <Select value={value || ""} onValueChange={(v) => onChange(fk, v)}>
          <SelectTrigger data-testid={testId} className="rounded-sm border-[#D1D5DB] focus:border-[#025979] focus:ring-[#025979]">
            <SelectValue placeholder={question.placeholder || `Select ${question.question_text.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : question.answer_type === "number" || question.answer_type === "optional_number" ? (
        <div className="flex items-center gap-2 max-w-xs">
          <Input
            data-testid={testId}
            type="number"
            value={value ?? ""}
            placeholder={question.placeholder || ""}
            min={question.min_value ?? undefined}
            max={question.max_value ?? undefined}
            onChange={(e) => onChange(fk, e.target.value)}
            className="rounded-sm border-[#D1D5DB]"
          />
          {question.unit && (
            <span className="text-sm text-[#6B7280] shrink-0">{question.unit}</span>
          )}
        </div>
      ) : question.answer_type === "text" ? (
        <Input data-testid={testId} value={value || ""} placeholder={question.placeholder || ""} onChange={(e) => onChange(fk, e.target.value)} className="rounded-sm border-[#D1D5DB]" />
      ) : (
        <CardSelect options={opts} value={value} onChange={(v) => onChange(fk, v)} testId={testId} multi={isMulti} />
      )}
      {error && <p data-testid={`error-${fk}`} className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// Group a section's questions by their admin-managed `subsection_heading`,
// preserving display order. Questions with no heading stay in the first
// (unlabelled) group so existing Step 2 layout is unchanged; questions sharing
// a heading (e.g. "Current SAP Landscape") render under an inline sub-header.
function groupBySubsection(questions) {
  const groups = [];
  const seen = new Map();
  for (const q of questions) {
    const key = q.subsection_heading || "";
    if (!seen.has(key)) {
      const entry = { heading: key, questions: [] };
      seen.set(key, entry);
      groups.push(entry);
    }
    seen.get(key).questions.push(q);
  }
  return groups;
}

export default function Module2Page() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  // Snapshot of the persisted module_data.module2 — used so Step 2's save never
  // overwrites fields collected elsewhere (e.g. the Step 1 "Assessment Context"
  // questions: current_sap_solution, preferred_hosting_model, etc.).
  const [savedModule2, setSavedModule2] = useState({});
  // Selected assessment type — drives the Cloud Migration page title.
  const [scenario, setScenario] = useState("");
  // Contributors share the owner's assessment; Step 2 is read-only for them.
  const [readOnly, setReadOnly] = useState(false);
  // Warehouse Groups (owner-defined assessment units) — persisted top-level on
  // the assessment as `warehouse_groups`. Legacy docs default to [].
  const [groups, setGroups] = useState([]);
  const [groupsError, setGroupsError] = useState("");
  const [warehouseTypeOptions, setWarehouseTypeOptions] = useState([]);
  // Group-editor option lists sourced from the Admin question bank (not
  // hardcoded) so they never drift from Admin: Current Warehouse Solution +
  // Preserve Architecture. Warehouse Type options come from the bank too.
  const [solutionOptions, setSolutionOptions] = useState([]);
  const [preserveArchOptions, setPreserveArchOptions] = useState([]);
  // A legacy assessment (already progressed to Step 3) is exempt from the
  // "≥1 group required" rule so existing evaluations continue/resume freely.
  // Warehouse Groups are required for every evaluation before Step 3.

  // Load questions from API (+ merge saved module2 answers for resume)
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getQuestionsGrouped("module2");
        let rawSections = data.sections || {};

        // Group-editor option lists — sourced from the Admin question bank
        // (regardless of active status) so Warehouse Type / Current Solution /
        // Preserve Architecture never become hardcoded lists that drift.
        // Warehouse Type options come from the retired "representative_sites"
        // question (its option set is the canonical warehouse-type list).
        try {
          const opts = await getQuestionFieldOptions([
            "representative_sites", "current_sap_solution", "preserve_architecture_importance",
          ]);
          setWarehouseTypeOptions(opts.representative_sites?.answer_options || []);
          setSolutionOptions(opts.current_sap_solution?.answer_options || []);
          setPreserveArchOptions(opts.preserve_architecture_importance?.answer_options || []);
        } catch (e) {
          console.error("Failed to load group field options:", e);
          const whq = Object.values(rawSections).flat().find((q) => q.field_key === "representative_sites");
          setWarehouseTypeOptions(whq?.answer_options || []);
        }

        // Build initial form shape from question bank (apply admin defaults)
        const initial = {};
        Object.values(rawSections).flat().forEach((q) => {
          if (q.field_key) {
            initial[q.field_key] =
              q.answer_type === "multi_select" ? [] : (q.default_value || "");
          }
        });

        // Resume support: merge previously saved module2 answers.
        // Only rehydrate when BOTH the draft email AND assessment_id are
        // present (guards against a stale email left behind after submission).
        // Also skip submitted/completed assessments — they are read-only.
        const email = localStorage.getItem("warehousex_email");
        const draftId = localStorage.getItem("warehousex_assessment_id");
        let scenario = "";
        if (email && draftId) {
          try {
            let assessment = await getAssessment(email);
            // Contributor: owner-scoped lookup misses their email — load the
            // SAME shared assessment by id so Step 2 shows the owner's data.
            if (!assessment) assessment = await getAssessmentById(draftId);
            const isClosed = assessment?.status === "submitted" || assessment?.status === "completed";
            const ownerEmail = (assessment?.email || "").trim().toLowerCase();
            setReadOnly(!isClosed && !!ownerEmail && (email || "").trim().toLowerCase() !== ownerEmail);
            if (!isClosed) scenario = assessment?.scenario_type || "";
            const saved = !isClosed ? assessment?.module_data?.module2 : null;
            if (saved && typeof saved === "object") {
              setSavedModule2(saved);
              Object.entries(saved).forEach(([k, v]) => {
                if (k in initial) initial[k] = v;
              });
            }
            // Warehouse Groups + legacy detection.
            if (Array.isArray(assessment?.warehouse_groups)) {
              setGroups(assessment.warehouse_groups);
            }
          } catch { /* ignore — fresh form */ }
        }

        // Admin-driven: keep only questions whose evaluation_types include the
        // selected assessment type. Missing → greenfield+brownfield. When no
        // scenario is known yet, nothing is filtered out.
        // representative_sites (the old enterprise "Warehouse Types" question)
        // is retired in the bank (active=false) so it no longer appears in
        // Step 2. Kept in this hide list as belt-and-suspenders in case it is
        // ever reactivated in Admin — Warehouse Type(s) live per group now.
        const HIDDEN_M2_FIELDS = ["representative_sites"];
        const filteredSections = {};
        Object.entries(rawSections).forEach(([sec, qs]) => {
          const kept = (qs || []).filter(
            (q) => questionAppliesToScenario(q, scenario) && !HIDDEN_M2_FIELDS.includes(q.field_key)
          );
          if (kept.length > 0) filteredSections[sec] = kept;
        });
        setSections(filteredSections);
        setScenario(scenario);

        setForm(initial);
      } catch (e) {
        console.error("Failed to load questions:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  // Owner group mutations persist immediately (whole-array replace) so a page
  // reload keeps them, then also re-saved on Continue for safety.
  const persistGroups = (next) => {
    setGroups(next);
    if (groupsError && next.length > 0) setGroupsError("");
    const assessmentId = localStorage.getItem("warehousex_assessment_id");
    if (!readOnly && assessmentId) {
      updateAssessment(assessmentId, { warehouse_groups: next }).catch((e) => {
        console.error("Failed to save warehouse groups:", e);
        toast.error("Couldn't save your warehouse groups. Please check your connection and try again.");
      });
    }
  };

  // Get warehouse type options for representative sites
  const validate = () => {
    const e = {};
    Object.values(sections).flat().forEach((q) => {
      if (!q.field_key || q.field_key === "representative_sites") return;
      // Honour the admin-managed required flag — optional questions (e.g. the
      // Current SAP Landscape data-capture fields) never block Continue.
      if (q.is_required === false) return;
      const val = form[q.field_key];
      if (q.answer_type === "multi_select") {
        if (!val || !val.length) e[q.field_key] = `Select at least one`;
      } else if (!val) {
        e[q.field_key] = `This field is required`;
      }
    });
    return e;
  };

  const handleContinue = async () => {
    // Contributors can't edit Step 2 — just move forward on the shared
    // assessment (id + their email in localStorage stay untouched).
    if (readOnly) { navigate("/evaluate/module-3"); return; }
    const errs = validate();
    // Every evaluation (all assessment types, manual + demo) requires at least
    // one Warehouse Evaluation Group before Step 3.
    let groupErr = "";
    if (groups.length === 0) {
      groupErr = "Add at least one warehouse group to continue.";
      setGroupsError(groupErr);
    }
    if (Object.keys(errs).length > 0 || groupErr) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      const assessmentId = localStorage.getItem("warehousex_assessment_id");
      const email = localStorage.getItem("warehousex_email");
      if (assessmentId) {
        await updateAssessment(assessmentId, {
          current_step: 2,
          progress_percent: 40,
          warehouse_groups: groups,
          module_data: { module2: { ...savedModule2, ...form } },
        });
      }
      trackEvent("module2_completed", email, { industry: form.industry });
      navigate("/evaluate/module-3");
    } catch (e) {
      console.error("Failed to save:", e);
      setErrors({ _general: "Failed to save. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-sm text-[#6B7280]">Loading questions...</p>
        </main>
      </div>
    );
  }

  const sectionOrder = Object.keys(sections);

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Header />

      <StepProgress currentStep={2} />

      <main className="flex-1 pt-8 sm:pt-12 pb-12">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Demo profile applied banner — shared across Module 1/2/3. */}
          <DemoProfileBanner stepNumber={2} />
          <div className="mb-6">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5">
                <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                <span className="text-xs font-medium text-[#025979]">Step 2 — Warehouse Landscape</span>
              </div>
              <div
                data-testid="module2-ai-cue"
                className="hidden sm:inline-flex items-center gap-1 text-[11px] text-[#6B7280]"
              >
                <Sparkles className="w-3 h-3 text-[#6E5BCE]" strokeWidth={1.75} />
                Your inputs dynamically shape the evaluation
              </div>
            </div>
            <p
              data-testid="module2-step-indicator"
              className="text-[11px] text-[#9CA3AF] mb-4 tracking-wide"
            >
              Step 2 of 3 • Takes ~2–3 minutes
            </p>
            <h1 data-testid="module2-title" className="text-xl sm:text-2xl font-bold tracking-tight text-[#1F2937]" style={{ fontFamily: "Chivo, sans-serif" }}>
              {scenario === "cloud_migration"
                ? "Understand Your Current SAP Landscape"
                : "Understand Your Warehouse Landscape"}
            </h1>
            <p data-testid="module2-subtitle" className="mt-2 text-sm text-[#6B7280] leading-relaxed max-w-2xl">
              We'll use this information to understand your warehouse network and
              tailor the right SAP strategy for your business.
            </p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">
            {readOnly && (
              <div data-testid="module2-readonly-banner" className="px-6 sm:px-8 py-2.5 text-xs font-medium text-[#025979] bg-[#E0F2FE]/50 border-b border-[#025979]/10">
                Contributor view — Step 2 is read-only. Only the evaluation owner can edit this setup.
              </div>
            )}
            <fieldset disabled={readOnly} className={`min-w-0 border-0 p-0 m-0${readOnly ? " pointer-events-none opacity-95" : ""}`}>
            {sectionOrder.map((sectionKey, sIdx) => {
              const sectionQuestions = sections[sectionKey] || [];
              const title = SECTION_LABELS[sectionKey] || sectionKey;
              const hasAnyAnswer = sectionQuestions.some((q) => {
                const v = form[q.field_key];
                if (Array.isArray(v)) return v.length > 0;
                return v !== undefined && v !== null && v !== "";
              });
              return (
                <div key={sectionKey} data-testid={`module2-section-${sectionKey}`}>
                  {sIdx > 0 && <div className="border-t border-[#E5E7EB]"></div>}
                  <div className="p-6 sm:p-8">
                    <div className="flex items-center gap-2 mb-5">
                      <div className="w-4 h-4 rounded-sm bg-[#025979]/10 flex items-center justify-center">
                        <span className="text-[8px] font-bold text-[#025979]">{sIdx + 1}</span>
                      </div>
                      <h2 className="text-sm font-semibold text-[#111827] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
                        {title}
                      </h2>
                    </div>
                    {groupBySubsection(sectionQuestions).map((grp, gi) => (
                      <div key={gi}>
                        {grp.heading && (
                          <div
                            className="mt-2 mb-4 pb-1.5 border-b border-[#E5E7EB]"
                            data-testid={`module2-subheading-${grp.heading.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#025979]">
                              {grp.heading}
                            </p>
                          </div>
                        )}
                        {grp.questions.map((q) => (
                          <DynamicField key={q.id} question={q} value={form[q.field_key]} onChange={update} errors={errors} />
                        ))}
                      </div>
                    ))}
                    {hasAnyAnswer && (
                      <p
                        data-testid={`section-ack-${sectionKey}`}
                        className="mt-3 flex items-center gap-1.5 text-[11px] text-[#025979]"
                      >
                        <Check className="w-3 h-3" strokeWidth={2.5} />
                        Got it — this helps us refine your evaluation.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Warehouse Evaluation Groups — replaces Representative Warehouse
                Selection. Owner-managed; read-only for contributors. */}
            <div className="border-t border-[#E5E7EB]"></div>
            <div className="p-6 sm:p-8">
              <WarehouseGroups
                groups={groups}
                onChange={persistGroups}
                warehouseTypeOptions={warehouseTypeOptions}
                solutionOptions={solutionOptions}
                preserveArchOptions={preserveArchOptions}
                step1Scenario={scenario}
                readOnly={readOnly}
                error={groupsError}
              />
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* AI Guidance */}
            <div className="p-6 sm:p-8">
              <div data-testid="module2-ai-guidance" className="flex items-start gap-2.5 bg-[#F0F9FF] border border-[#025979]/15 rounded-sm px-4 py-3">
                <Sparkles className="w-4 h-4 text-[#6E5BCE] mt-0.5 shrink-0" strokeWidth={1.75} />
                <span className="text-xs text-[#374151] leading-relaxed">
                  Based on your overall warehouse landscape and Warehouse
                  Evaluation Groups, we'll evaluate the optimal SAP warehouse
                  architecture, including potential hybrid scenarios.
                </span>
              </div>
            </div>

            </fieldset>

            <div className="border-t border-[#E5E7EB]"></div>

            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-3">
              <Button data-testid="module2-back-btn" variant="outline" onClick={() => navigate("/evaluate/module-1")} className="rounded-sm text-sm border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors">
                <ArrowLeft className="mr-2 w-4 h-4" />Back
              </Button>
              {errors._general && <p className="text-xs text-red-500">{errors._general}</p>}
              <Button data-testid="module2-continue-btn" onClick={handleContinue} disabled={submitting} className="bg-[#025979] hover:bg-[#03738C] text-white px-8 py-2.5 text-sm font-medium rounded-sm transition-colors shadow-sm disabled:opacity-50">
                {submitting ? "Saving..." : (scenario === "cloud_migration" ? "Continue to Migration Assessment" : "Continue to Process Evaluation")}
                {!submitting && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
